import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { PreloadingStrategy, Route, Router, isActive } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { isWebsiteLanguage } from './i18n';

/**
 * Route data read by {@link IdlePreload}. `preload: false` keeps a route's
 * chunk out of the idle queue: pages nobody reaches from the running one
 * (the internal showcase, the micro-sites) are a download the visitor pays
 * for and never spends.
 */
export type PreloadRouteData = {
  preload: boolean;
};

/**
 * `NetworkInformation` is not in `lib.dom` and Safari and Firefox implement
 * none of it, so the two fields that gate preloading are declared where they
 * are read and treated as optional everywhere else.
 */
type NavigatorWithConnection = Navigator & {
  connection?: {
    saveData?: boolean;
    effectiveType?: string;
  };
};

/**
 * Effective types where a speculative download would compete with the page the
 * visitor is actually reading. `3g` is not one of them: it still pulls a route
 * chunk down faster than a person picks the next link.
 */
const METERED_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

function isMeteredConnection(): boolean {
  const { connection } = navigator as NavigatorWithConnection;

  return (
    connection?.saveData === true ||
    METERED_EFFECTIVE_TYPES.has(connection?.effectiveType ?? '')
  );
}

/**
 * One idle slot, as a cancellable observable: unsubscribing before it fires
 * drops the callback, so a preload that is no longer wanted never starts.
 */
function whenIdle$(): Observable<void> {
  return new Observable<void>(subscriber => {
    const emit = (): void => {
      subscriber.next();
      subscriber.complete();
    };

    // Zoneless apps no longer patch these timers, so the native APIs are used
    // directly. Safari only shipped `requestIdleCallback` in 18.4.
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(emit);

      return () => {
        cancelIdleCallback(handle);
      };
    }

    const handle = setTimeout(emit);

    return () => {
      clearTimeout(handle);
    };
  });
}

@Injectable({ providedIn: 'root' })
export class IdlePreload implements PreloadingStrategy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);

  preload(route: Route, fn$: () => Observable<unknown>): Observable<unknown> {
    if (!isPlatformBrowser(this.platformId) || !this.shouldPreload(route)) {
      return of(null);
    }

    // Returning the load rather than firing and forgetting it: the router
    // walks the routes a lazy tree brings with it only through this
    // subscription, and an unsubscribe (a destroyed injector, a preloader
    // torn down) cancels work that is still queued.
    return whenIdle$().pipe(switchMap(fn$));
  }

  private shouldPreload(route: Route): boolean {
    if (
      (route.data as Partial<PreloadRouteData> | undefined)?.preload === false
    ) {
      return false;
    }

    return !this.isDormantLanguage(route) && !isMeteredConnection();
  }

  /**
   * The language branches are two full copies of the site, and a visitor reads
   * one of them. The branch that is not under the current URL waits until a
   * navigation puts it there.
   */
  private isDormantLanguage(route: Route): boolean {
    return (
      isWebsiteLanguage(route.path) &&
      !isActive(`/${route.path}`, this.router)()
    );
  }
}
